import React, { useMemo } from 'react';
import SupportAgent from '@mui/icons-material/SupportAgent';
import Storefront from '@mui/icons-material/Storefront';
import Casino from '@mui/icons-material/Casino';
import CardGiftcard from '@mui/icons-material/CardGiftcard';
import Token from '@mui/icons-material/Token';
import ManageAccounts from '@mui/icons-material/ManageAccounts';
import Payment from '@mui/icons-material/Payment';
import AccountBalance from '@mui/icons-material/AccountBalance';
import BarChart from '@mui/icons-material/BarChart';
import Insights from '@mui/icons-material/Insights';
import Poll from '@mui/icons-material/Poll';
import AutoStories from '@mui/icons-material/AutoStories';
import RocketLaunch from '@mui/icons-material/RocketLaunch';
import Dataset from '@mui/icons-material/Dataset';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import Science from '@mui/icons-material/Science';
import LocalDrink from '@mui/icons-material/LocalDrink';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Home from '@mui/icons-material/Home';
import { Box, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import UniversalSidebar, { SidebarSection, SidebarItem } from './UniversalSidebar';
import { RoleChip } from '../ui';
import ServerSelection from '../ServerSelection';
import { useAuth } from '../../contexts/AuthContext';
import { canAccessAdminPage } from '../../config/adminPermissions';

interface AdminSidebarProps {
  mobileOpen: boolean;
  onDrawerToggle: () => void;
  /** «Вернуться в кабинет» не влезла в шапку — показать её первым пунктом меню */
  showBackToDashboard?: boolean;
  /** Сайдбар свёрнут на десктопе, чтобы контент занимал всю ширину */
  collapsed?: boolean;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({
  mobileOpen,
  onDrawerToggle,
  showBackToDashboard,
  collapsed = false,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  // Тот же порог, что у бургера: ниже 870px шапка сворачивается и «Главная» из неё пропадает
  const isMobile = useMediaQuery('(max-width: 869px)');

  // Бейдж роли: тот же чип, что в списке пользователей и конфигах лаунчера.
  // Раньше здесь был сплошной красный градиент с подписью «ADMIN» независимо от роли —
  // и выбивался из вида, и врал владельцу с поддержкой.
  // useMemo: иначе новый JSX на каждый рендер сбрасывает мемоизацию секций сайдбара
  const AdminInfo = useMemo(() => (
    <Box sx={{ textAlign: 'center', mb: 2 }}>
      {user?.role && (
        <RoleChip role={user.role} label={t(`admin:users.roles.${user.role.toLowerCase()}`)} />
      )}
    </Box>
  ), [t, user?.role]);

  // Admin menu items — filtered by role via config
  const menuItems = useMemo((): SidebarItem[] => {
    const allItems: SidebarItem[] = [
      { id: 'users', text: t('admin:navigation.usersManagement'), icon: <ManageAccounts />, path: '/admin/users' },
      { id: 'support', text: t('admin:navigation.supportManagement'), icon: <SupportAgent />, path: '/admin/support' },
      { id: 'payments', text: t('admin:navigation.payments'), icon: <AccountBalance />, path: '/admin/payments' },
      { id: 'analytics', text: t('admin:navigation.analytics'), icon: <Insights />, path: '/admin/analytics' },
      { id: 'donation-bonuses', text: t('admin:navigation.donationBonuses'), icon: <Payment />, path: '/admin/donation-bonuses' },
      { id: 'shop', text: t('admin:navigation.shopManagement'), icon: <Storefront />, path: '/admin/shop' },
      { id: 'promo-codes', text: t('admin:navigation.promoCodes'), icon: <Token />, path: '/admin/promo-codes' },
      { id: 'promo-stats', text: t('admin:navigation.promoStats'), icon: <BarChart />, path: '/admin/promo-stats' },
      { id: 'roulette', text: t('admin:navigation.rouletteManagement'), icon: <Casino />, path: '/admin/roulette' },
      { id: 'roulette-stats', text: t('admin:navigation.rouletteStats'), icon: <BarChart />, path: '/admin/roulette-stats' },
      { id: 'polls', text: t('admin:navigation.polls'), icon: <Poll />, path: '/admin/polls' },
      { id: 'posts', text: t('admin:navigation.posts'), icon: <AutoStories />, path: '/admin/posts' },
      { id: 'referrals', text: t('admin:navigation.referralManagement'), icon: <CardGiftcard />, path: '/admin/referrals' },
      { id: 'launcher-configs', text: t('admin:navigation.launcherConfigs'), icon: <RocketLaunch />, path: '/admin/launcher-configs' },
    ];
    const items = allItems.filter(item => canAccessAdminPage(user?.role, item.id));

    // Игровые данные — раскрывающаяся группа: таблиц впереди ~20, плоским списком меню
    // станет нечитаемым. Доступ проверяется у КАЖДОЙ таблицы; пустая группа не показывается.
    const gameDataItems: SidebarItem[] = [
      {
        id: 'game-data/ornaments',
        text: t('admin:navigation.gameDataOrnaments'),
        icon: <AutoAwesome />,
        path: '/admin/game-data/ornaments',
      },
      {
        id: 'game-data/synthesis',
        text: t('admin:navigation.gameDataSynthesis'),
        icon: <Science />,
        path: '/admin/game-data/synthesis',
      },
      {
        id: 'game-data/alchemy',
        text: t('admin:navigation.gameDataAlchemy'),
        icon: <LocalDrink />,
        path: '/admin/game-data/alchemy',
      },
    ].filter(item => canAccessAdminPage(user?.role, item.id));

    if (gameDataItems.length > 0) {
      items.push({
        id: 'game-data',
        text: t('admin:navigation.gameData'),
        icon: <Dataset />,
        subItems: gameDataItems,
      });
    }

    // Не влезла в шапку — уходит сюда первым пунктом, иначе выхода из админки на узком
    // экране просто нет: в самом меню ссылок наружу нет.
    if (showBackToDashboard) {
      items.unshift({
        id: 'back-to-dashboard',
        text: t('admin:navigation.backToDashboard'),
        icon: <ArrowBack />,
        path: '/dashboard',
      });
    }

    // «Главная» живёт в центре шапки и на узком экране прячется вместе с ней — как в кабинете,
    // где быстрые ссылки на мобиле уезжают в конец меню (AppSidebar)
    if (isMobile) {
      items.push({
        id: 'home',
        text: t('navigation:menu.home'),
        icon: <Home />,
        path: '/',
      });
    }
    return items;
  }, [t, user?.role, showBackToDashboard, isMobile]);

  // Sidebar sections
  const sections = useMemo((): SidebarSection[] => [
    {
      id: 'admin-info-section',
      customContent: AdminInfo,
      fixed: true,
    },
    {
      id: 'server-section',
      customContent: <ServerSelection />,
      fixed: true,
    },
    {
      id: 'menu-section',
      withMenuSection: true,
      items: menuItems,
    },
  ], [menuItems, AdminInfo]);

  return (
    <UniversalSidebar
      sections={sections}
      mobileOpen={mobileOpen}
      onDrawerToggle={onDrawerToggle}
      storageKey="adminSidebarExpanded"
      collapsed={collapsed}
    />
  );
};

export default AdminSidebar;